import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Upload, Download, FileSpreadsheet, CheckCircle, AlertCircle } from 'lucide-react';
import Card from '../../components/Common/Card';
import Button from '../../components/Common/Button';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';

const BulkUploadStudents = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      validateAndSetFile(selectedFile);
    }
  };

  const validateAndSetFile = (selectedFile) => {
    const validTypes = [
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/csv',
      'application/csv'
    ];

    if (validTypes.includes(selectedFile.type) || 
        selectedFile.name.endsWith('.xlsx') || 
        selectedFile.name.endsWith('.xls') || 
        selectedFile.name.endsWith('.csv')) {
      setFile(selectedFile);
      setUploadResult(null);
    } else {
      toast.error('Please upload a valid Excel file (.xlsx, .xls, or .csv)');
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      validateAndSetFile(droppedFile);
    }
  };

  const handleDownloadTemplate = () => {
    const headers = ['Full Name', 'Email', 'Mobile', 'Roll Number', 'Date of Birth', 'Gender'];
    const sampleData = [
      `John Student,john.student@example.com,1234567890,2024001,2002-05-15,male`,
      `Jane Student,jane.student@example.com,0987654321,2024002,2001-09-22,female`
    ];

    const csvContent = [headers.join(','), ...sampleData].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'students_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);

    toast.success('Template downloaded successfully!');
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Please select a file first');
      return;
    }

    setLoading(true);
    setUploadResult(null); // Clear previous results

    const formData = new FormData();
    formData.append('file', file);
    formData.append('college', user.college);
    formData.append('role', 'student');

    try {
      toast.loading('Uploading students...', { id: 'upload' });

      const { data } = await api.post('/users/bulk-upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (data.success) {
        setUploadResult(data.data);
        toast.success(`Successfully uploaded ${data.data.successful} students!`, { id: 'upload' });

        // Only navigate if all uploads were successful
        if (data.data.failed === 0) {
          setTimeout(() => {
            navigate('/college/students');
          }, 2000);
        }
      }
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to upload file';
      toast.error(message, { id: 'upload' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <button
          onClick={() => navigate('/college/dashboard')}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-display font-bold text-gray-900">Bulk Upload Students</h1>
          <p className="text-gray-600 mt-1">Upload multiple students using Excel file</p>
        </div>
      </div>

      {/* Instructions */}
      <Card title="Instructions">
        <div className="space-y-3">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0 w-6 h-6 bg-secondary-100 text-secondary-600 rounded-full flex items-center justify-center text-sm font-semibold">
              1
            </div>
            <p className="text-gray-700">Download the template file by clicking the button below</p>
          </div>
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0 w-6 h-6 bg-secondary-100 text-secondary-600 rounded-full flex items-center justify-center text-sm font-semibold">
              2
            </div>
            <p className="text-gray-700">Fill in student details (Full Name, Email, Mobile, Roll Number, Date of Birth, Gender)</p>
          </div>
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0 w-6 h-6 bg-secondary-100 text-secondary-600 rounded-full flex items-center justify-center text-sm font-semibold">
              3
            </div>
            <p className="text-gray-700">Upload the completed file</p>
          </div>
        </div>

        <div className="mt-6 space-y-4">
          <Button
            variant="outline"
            icon={Download}
            onClick={handleDownloadTemplate}
          >
            Download Template
          </Button>
        </div>
      </Card>

      {/* Upload Section */}
      <Card title="Upload File">
        <div className="space-y-6">
          <label
            htmlFor="file-upload"
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={`block border-2 border-dashed rounded-xl p-12 text-center transition-all cursor-pointer group ${
              isDragging 
                ? 'border-secondary-500 bg-secondary-50 scale-[1.02] shadow-lg' 
                : 'border-gray-300 hover:border-secondary-500 hover:bg-secondary-50'
            }`}
          >
            <div className={`transition-transform duration-300 ${isDragging ? 'scale-110' : ''}`}>
              <FileSpreadsheet className={`w-16 h-16 mx-auto mb-4 transition-colors ${
                isDragging ? 'text-secondary-600' : 'text-gray-400 group-hover:text-secondary-500'
              }`} />
            </div>
            <input
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={handleFileChange}
              className="hidden"
              id="file-upload"
            />
            <span className="text-secondary-600 group-hover:text-secondary-700 font-medium">
              Click to upload
            </span>
            <p className="text-gray-500 text-sm mt-2">or drag and drop</p>
            <p className="text-gray-400 text-xs mt-1">Excel files (.xlsx, .xls, .csv)</p>

            {file && (
              <div className="mt-4 inline-flex items-center px-4 py-2 bg-secondary-50 text-secondary-700 rounded-lg border border-secondary-100 animate-fade-in">
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                <span className="text-sm font-medium">{file.name}</span>
              </div>
            )}
          </label>

          <div className="flex justify-end space-x-4">
            <Button
              variant="outline"
              onClick={() => navigate('/college/dashboard')}
            >
              Cancel
            </Button>
            <Button
              variant="secondary"
              icon={Upload}
              onClick={handleUpload}
              loading={loading}
              disabled={!file}
            >
              Upload Students
            </Button>
          </div>
        </div>
      </Card>

      {/* Upload Result */}
      {uploadResult && (
        <Card title="Upload Results">
          <div className="space-y-4">
            <div className="flex items-center space-x-3 p-4 bg-green-50 rounded-lg">
              <CheckCircle className="w-6 h-6 text-green-600" />
              <div>
                <p className="font-semibold text-green-900">Successfully uploaded: {uploadResult.successful}</p>
                <p className="text-sm text-green-700">Students have been added to your institution</p>
              </div>
            </div>

            {uploadResult.failed > 0 && (
              <div className="flex items-center space-x-3 p-4 bg-red-50 rounded-lg">
                <AlertCircle className="w-6 h-6 text-red-600" />
                <div>
                  <p className="font-semibold text-red-900">Failed: {uploadResult.failed}</p>
                  <p className="text-sm text-red-700">Some entries could not be processed</p>
                </div>
              </div>
            )}

            {uploadResult.errors && uploadResult.errors.length > 0 && (
              <div className="mt-4">
                <p className="font-semibold text-gray-900 mb-2">Error Details:</p>
                <ul className="space-y-1">
                  {uploadResult.errors.map((error, index) => (
                    <li key={index} className="text-sm text-red-600">• {error}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </Card>
      )}
    </div>
  );
};

export default BulkUploadStudents;
